import { useContext, createContext, useEffect, useState } from "react"
import { useApolloClient } from "@apollo/react-hooks"
import moment from "moment"

import { ANNUAL_PLUS } from "lib/tools/subscriptions-list"
import { useAuth } from "lib/auth"
import { useCountry } from "lib/country"
import {
	FETCH_ACCESSES_SVOD,
	FETCH_ACCESSES_AMOUNT,
	FETCH_ACCESS,
	FETCH_SVOD_MOVIES_IDS,
	FETCH_USER_SUBSCRIPTIONS
} from "lib/graphql"
import { MONTHLY } from "lib/tools/subscriptions-list"
import { wait } from "lib/wait"

const debug = require("debug")("lacinetek:accesses")

const accessesMap = new Map()

const accessesContext = createContext()

export const ProvideAccesses = ({ children, country, countries }) => {
	const accesses = useProvideAccesses({ country, countries })

	return <accessesContext.Provider value={accesses}>{children}</accessesContext.Provider>
}

export const useAccesses = () => {
	return useContext(accessesContext)
}

function useProvideAccesses({ country, countries }) {
	const [hasSubscribed, setHasSubscribed] = useState(null)
	const [subscription, setSubscription] = useState(null)
	const [hasSubscribedRest, setHasSubscribedRest] = useState(null)
	const [restSubscription, setRestSubscription] = useState(null)
	const [hasAccesses, setHasAccesses] = useState(null)
	const [svodMoviesIds, setSvodMoviesIds] = useState([])

	const client = useApolloClient()
	const {
		user,
		onSignedIn,
		removeOnSignedIn,
		onSignedOut,
		removeOnSignedOut
	} = useAuth()
	const { inEuro, filterCountry } = useCountry()

	useEffect(() => {
		onSignedIn("subscription", fetchSubscription)
		onSignedIn("restSubscription", fetchRestSubscription)
		onSignedIn("hasAccesses", checkHasAccesses)
		onSignedOut("reset-accesses", reset)
		onSignedIn("fetchSVODMoviesIds", fetchSVODMoviesIds)

		return () => {
			removeOnSignedIn("subscription")
			removeOnSignedIn("restSubscription")
			removeOnSignedIn("hasAccesses")
			removeOnSignedOut("reset-accesses")
			removeOnSignedIn("fetchSVODMoviesIds")
		}
	}, [])

	const reset = () => {
		setHasSubscribed(null)
		setHasSubscribedRest(null)
		setSubscription(null)
		setRestSubscription(null)
		setSvodMoviesIds([])
		accessesMap.clear()
	}

	const fetchSVODMoviesIds = () => {
		return client.query({
			query: FETCH_SVOD_MOVIES_IDS,
			variables: { query: "abonnement-fr" }
		})
			.then(({ data }) => {
				const movies = data.cms.categories.items[0].children.items.map(rails => {
					const directorMovies = rails.children.items.map(director => {
						return director.products.items
					})

					if (directorMovies.length > 0) {
						return directorMovies.flat()
					}
				}).flat()

				setSvodMoviesIds(movies)
			})
			.catch(error => console.log(error))
	}

	const fetchRestSubscription = user => {
		if (!user) {
			return Promise.resolve()
		}

		return client.query({
			query: FETCH_USER_SUBSCRIPTIONS,
			variables: { userId: parseInt(user.profile.id) },
			context: { clientName: "admin" }
		})
			.then(({ data }) => {
				if (!data || !data.subscriptions) {
					return
				}

				let currentHasSubscribed = false
				let currentSubscription

				for (const subscription of data.subscriptions) {
					if (subscription.dateExp < moment().format("YYYY-MM-DD")) {
						continue
					}

					currentHasSubscribed = true
					currentSubscription = subscription
				}

				if (currentHasSubscribed) {
					setRestSubscription(currentSubscription)
					setHasSubscribedRest(true)
				} else {
					setHasSubscribedRest(false)
				}
			})
	}

	const fetchSubscription = (user) => {
		if (!user) {
			return Promise.resolve()
		}

		const yesterday = moment().subtract(1, 'd').format("YYYY-MM-DD");
		const products = country.subscriptions.map(subscription => subscription.id)

		return client
			.query({
				query: FETCH_ACCESSES_SVOD,
				variables: {
					query: `type: SVOD AND dateExp: {${yesterday} TO *}  AND product: (${products.join(" OR ")})`
				},
				fetchPolicy: "network-only"
			})
			.then(({ data }) => {
				if (!data.user) {
					setHasSubscribed(null)
					return
				}

				if (data.user.accesses.items.length < 1 || data.user.accesses.items[0].dateExp < moment().format("YYYY-MM-DD")) {
					setHasSubscribed(false)
					return
				}

				setSubscription(mapUserAccess(data.user.accesses.items[0]))
				setHasSubscribed(true)
			})
	}

	const refetchSubscription = (user) => {
		setHasSubscribed(null)
		accessesMap.clear()
		return fetchSubscription(user)
	}

	const mapUserAccess = (access) => {
		const countrySubscription = filterCountry.subscriptions.find(countrySubscription => countrySubscription.id == access.product.id)
		const isMonthly = countrySubscription ? countrySubscription.key.includes(MONTHLY) : false

		return {
			...access,
			isMonthly,
			isocode: filterCountry.isoCode
		}
	}

	const hasAccessToProduct = (product) => {
		return new Promise((resolve, reject) => {
			let access = false

			if (!inEuro) {
				resolve(access)

				return
			}

			if (!user) {
				resolve(access)

				return
			}

			if (accessesMap.has(product.id)) {
				resolve(accessesMap.get(product.id))

				return
			}

			debug(`Fetch access to product ${product.id} (${product.name})`)
			const productIsInSVOD = svodMoviesIds.find(film => film.id === product.id)

			if (productIsInSVOD && hasSubscribed) {
				access = true

				accessesMap.set(product.id, access)
				resolve(access)
			}

			if (access === false) {
				client.query({
					query: FETCH_ACCESS,
					variables: {
						id: product.id
					}
				})
					.then(({ data }) => {
						const accessesForProduct = data.cms.products.items[0]?.extension.user?.accesses?.items
						const canAccessHDProduct = accessesForProduct.some(access => access.accessMode.quality === "HD")
						const accessibleVideo = data.cms.products.items[0]?.extension.videos?.items.find(video => canAccessHDProduct
							? video.accessInfo.qualityHD
							: video.accessInfo.qualitySD
						)

						if (accessesForProduct?.length !== 0 && accessibleVideo) {
							access = true
						}

						accessesMap.set(product.id, access)
						resolve(access)
					})
					.catch(error => console.log(error))
			}
		})
	}

	const hasAccessToAllProductsInBundle = async (bundle) => {
		if (!bundle || !bundle.extension || !bundle.extension.products) return false

		let hasTheAccesses = true

		for (const product of bundle.extension.products.items) {
			const res = await hasAccessToProduct(product)

			if (!res) {
				hasTheAccesses = false

				break
			}
		}

		return hasTheAccesses
	}

	const updateRecurringStateOfSubscription = (recurringState) => {
		setSubscription({
			...subscription,
			recurring: recurringState
		})
	}

	const waitAccessToProduct = async (product, options = {}) => {
		const { delay = 1000, max = 30 } = options
		let access = false

		for (let index = max; index > 0; index--) {
			debug(`waitAccessToProduct ${index}`)
			try {
				access = await hasAccessToProduct(product)
				debug(`waitAccessToProduct ${index} ${access}`)
			} catch (error) {
				throw error
			}

			if (access) {
				break
			}

			// Need to delete product access to correctly test next iteration
			deleteAccessToProduct(product)

			// Wait {delay} milliseconds till next product access test
			await wait(delay)
		}

		if (!access) {
			throw new Error("Wait access Product too long")
		}

		debug("End waitAccessToProduct")
	}

	const deleteAccessToProduct = (product) => {
		accessesMap.delete(product.id)
	}

	const isSubscriptionAnnualPlus = (subscription) => {
		const annuals = countries
			.flatMap(country => country.subscriptions)
			.filter(s => s.key == ANNUAL_PLUS)
			.map(s => s.id.toString())

		return annuals.includes(subscription.product.id)
	}

	const checkHasAccesses = async () => {
		const { data: { user: { accesses: { pagination: { total } } } } } = await client.query({
			query: FETCH_ACCESSES_AMOUNT,
			fetchPolicy: "network-only"
		})

		setHasAccesses(total > 0)
	}

	const isSubscriptionFromStripe = () => {
		if (
			subscription
			&& subscription.order.paymentGateway === "STRIPE"
		) {
			return true
		}

		return false
	}

	return {
		refetchSubscription,
		updateRecurringStateOfSubscription,
		hasAccessToProduct,
		hasAccessToAllProductsInBundle,
		waitAccessToProduct,
		deleteAccessToProduct,
		isSubscriptionAnnualPlus,
		isSubscriptionFromStripe,
		hasSubscribed,
		hasAccesses,
		subscription,
		hasSubscribedRest,
		restSubscription
	}
}
